import { Link } from 'react-router-dom'
import { ChevronRight, Gauge, Receipt } from 'lucide-react'
import { useUtilityTypes } from '@/hooks/use-utilities'
import { useBillingCycles, useGenerationRuns } from '@/hooks/use-billing'
import { Badge } from '@/components/ui/badge'
import { buildCycles, formatBillingPeriod, RunStatusBadge } from './billing-cycle-shared'

/** Level 0 of the drill-down — one card per utility type, each showing a quick read on its most
 * recent billing cycle. Click through to see every cycle for that type. */
export function BillingCyclesTab({ basePath }: { basePath: string }) {
  const { data: utilityData, isLoading } = useUtilityTypes()
  const { data: runsData } = useGenerationRuns()
  const { data: cyclesData } = useBillingCycles()

  const utilityTypes = utilityData?.data ?? []
  const runs = runsData?.data ?? []
  const persistedCycles = cyclesData?.data ?? []

  if (isLoading) {
    return <p className="font-sans text-sm text-muted-foreground">Loading utility types…</p>
  }

  if (utilityTypes.length === 0) {
    return (
      <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
        No utility types configured yet.
      </p>
    )
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {utilityTypes.map((type) => {
        const cycles = buildCycles(persistedCycles, runs, type.id)
        const latestCycle = cycles[0]
        return (
          <Link
            key={type.id}
            to={`${basePath}/cycles/${type.id}`}
            className="flex flex-col gap-2 rounded border border-border-2 bg-card p-4 transition-colors hover:border-ink"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {type.billingMethod === 'meter_based' ? (
                  <Gauge size={18} className="text-muted-foreground" aria-hidden />
                ) : (
                  <Receipt size={18} className="text-muted-foreground" aria-hidden />
                )}
                <span className="font-sans text-sm font-medium text-ink">{type.name}</span>
                {!type.active && <Badge variant="default">Inactive</Badge>}
              </div>
              <ChevronRight size={16} className="text-muted-foreground" />
            </div>
            <p className="font-sans text-xs text-muted-foreground">
              {type.billingMethod === 'meter_based' ? 'Meter-based' : 'Flat fee'} · {type.frequency}
            </p>
            {latestCycle ? (
              <div className="flex items-center gap-2">
                <span className="font-sans text-xs text-muted-foreground">
                  {formatBillingPeriod(latestCycle.period)}
                </span>
                <RunStatusBadge run={latestCycle.latest} />
              </div>
            ) : (
              <p className="font-sans text-xs text-muted-foreground">No billing cycles yet</p>
            )}
          </Link>
        )
      })}
    </div>
  )
}
